import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { graphqlFetch } from '@/utils/graphqlFetch';
import {
  CREATE_SOCIUM_INTEGRATION,
  LIST_SOCIUM_INTEGRATIONS,
  ROTATE_SOCIUM_INTEGRATION_KEY,
  SET_SOCIUM_INTEGRATION_STATUS,
  SOCIUM_DIAGNOSTICS,
  SOCIUM_FORMATION_DETAIL,
  SOCIUM_INTEGRATION_FORMATIONS,
  SOCIUM_INTEGRATION_LEARNERS,
  SOCIUM_INTEGRATION_SESSIONS,
  SOCIUM_INTEGRATION_STATS,
  SOCIUM_LEARNER_DETAIL,
  SOCIUM_SESSION_DETAIL,
  UPDATE_SOCIUM_INTEGRATION,
} from '../queries/socium';

export type SociumIntegration = {
  _id: string;
  name: string;
  environment: string;
  status: 'ACTIVE' | 'INACTIVE';
  apiKeyPrefix?: string;
  allowedRedirectDomains?: string[];
  createdAt?: string;
  school?: { _id: string; name: string } | null;
};

type IntegrationWithKey = {
  apiKey: string;
  integration: SociumIntegration;
};

export type SociumIntegrationStats = {
  integrationId: string;
  formationsCount: number;
  sessionsCount: number;
  usersProvisioned: number;
  learnersCount: number;
  modulesCount: number;
};

export type SociumDiagnosticLine = {
  label: string;
  value: string;
  level: 'ok' | 'warn' | 'error' | 'info';
};

export type SociumDiagnostic = {
  type: string;
  ref: string;
  status: 'found' | 'not_found' | 'conflict';
  lines: SociumDiagnosticLine[];
};

export const useSociumDiagnostics = () =>
  useMutation<
    SociumDiagnostic,
    Error,
    { integrationId: string; type: string; ref: string }
  >({
    mutationFn: async (variables) => {
      const data = await graphqlFetch<
        { sociumDiagnostics: SociumDiagnostic },
        typeof variables
      >(SOCIUM_DIAGNOSTICS, variables);
      return data.sociumDiagnostics;
    },
  });

export type CreateSociumIntegrationInput = {
  name: string;
  environment: string;
  schoolId?: string;
  schoolName?: string;
  regionId?: string;
  allowedRedirectDomains?: string[];
};

const SOCIUM_KEY = ['socium-integrations'];

export const useListSociumIntegrations = () =>
  useQuery<SociumIntegration[], Error>({
    queryKey: SOCIUM_KEY,
    queryFn: async () => {
      const data = await graphqlFetch<
        { listSociumIntegrations: SociumIntegration[] },
        null
      >(LIST_SOCIUM_INTEGRATIONS);
      return data.listSociumIntegrations;
    },
  });

export const useSociumIntegrationStats = (integrationId?: string) =>
  useQuery<SociumIntegrationStats, Error>({
    queryKey: ['socium-integration-stats', integrationId],
    enabled: !!integrationId,
    queryFn: async () => {
      const data = await graphqlFetch<
        { sociumIntegrationStats: SociumIntegrationStats },
        { integrationId: string }
      >(SOCIUM_INTEGRATION_STATS, { integrationId: integrationId as string });
      return data.sociumIntegrationStats;
    },
  });

export type SociumFormationRow = {
  externalRef: string;
  lessonId: string;
  title: string;
  isPublished: boolean;
  modulesCount: number;
  sessionsCount: number;
};

export type SociumSessionRow = {
  externalRef: string;
  classId: string;
  name: string;
  formationExternalRef?: string | null;
  formationTitle?: string | null;
  participantsCount: number;
};

export type SociumLearnerRow = {
  externalUserRef: string;
  userId: string;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  role: string;
  classesCount: number;
};

export const useSociumFormations = (integrationId?: string) =>
  useQuery<SociumFormationRow[], Error>({
    queryKey: ['socium-formations', integrationId],
    enabled: !!integrationId,
    queryFn: async () => {
      const data = await graphqlFetch<
        { sociumIntegrationFormations: SociumFormationRow[] },
        { integrationId: string }
      >(SOCIUM_INTEGRATION_FORMATIONS, { integrationId: integrationId as string });
      return data.sociumIntegrationFormations;
    },
  });

export const useSociumSessions = (integrationId?: string) =>
  useQuery<SociumSessionRow[], Error>({
    queryKey: ['socium-sessions', integrationId],
    enabled: !!integrationId,
    queryFn: async () => {
      const data = await graphqlFetch<
        { sociumIntegrationSessions: SociumSessionRow[] },
        { integrationId: string }
      >(SOCIUM_INTEGRATION_SESSIONS, { integrationId: integrationId as string });
      return data.sociumIntegrationSessions;
    },
  });

export const useSociumLearners = (integrationId?: string) =>
  useQuery<SociumLearnerRow[], Error>({
    queryKey: ['socium-learners', integrationId],
    enabled: !!integrationId,
    queryFn: async () => {
      const data = await graphqlFetch<
        { sociumIntegrationLearners: SociumLearnerRow[] },
        { integrationId: string }
      >(SOCIUM_INTEGRATION_LEARNERS, { integrationId: integrationId as string });
      return data.sociumIntegrationLearners;
    },
  });

export type SociumFormationDetail = {
  title: string;
  isPublished: boolean;
  modules: { title: string; isPublished: boolean }[];
  sessions: { externalRef: string; name: string; participantsCount: number }[];
};

export type SociumSessionDetail = {
  name: string;
  formationTitle?: string | null;
  participants: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    phoneNumber?: string | null;
    status: string;
  }[];
};

export type SociumLearnerDetail = {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  role: string;
  memberships: {
    sessionName: string;
    formationTitle?: string | null;
    status: string;
    progressPercent: number;
    completedModules: number;
    totalModules: number;
  }[];
};

export const useSociumFormationDetail = (
  integrationId?: string,
  externalRef?: string,
) =>
  useQuery<SociumFormationDetail, Error>({
    queryKey: ['socium-formation-detail', integrationId, externalRef],
    enabled: !!integrationId && !!externalRef,
    queryFn: async () => {
      const data = await graphqlFetch<
        { sociumFormationDetail: SociumFormationDetail },
        { integrationId: string; externalRef: string }
      >(SOCIUM_FORMATION_DETAIL, {
        integrationId: integrationId as string,
        externalRef: externalRef as string,
      });
      return data.sociumFormationDetail;
    },
  });

export const useSociumSessionDetail = (
  integrationId?: string,
  externalRef?: string,
) =>
  useQuery<SociumSessionDetail, Error>({
    queryKey: ['socium-session-detail', integrationId, externalRef],
    enabled: !!integrationId && !!externalRef,
    queryFn: async () => {
      const data = await graphqlFetch<
        { sociumSessionDetail: SociumSessionDetail },
        { integrationId: string; externalRef: string }
      >(SOCIUM_SESSION_DETAIL, {
        integrationId: integrationId as string,
        externalRef: externalRef as string,
      });
      return data.sociumSessionDetail;
    },
  });

export const useSociumLearnerDetail = (
  integrationId?: string,
  externalUserRef?: string,
) =>
  useQuery<SociumLearnerDetail, Error>({
    queryKey: ['socium-learner-detail', integrationId, externalUserRef],
    enabled: !!integrationId && !!externalUserRef,
    queryFn: async () => {
      const data = await graphqlFetch<
        { sociumLearnerDetail: SociumLearnerDetail },
        { integrationId: string; externalUserRef: string }
      >(SOCIUM_LEARNER_DETAIL, {
        integrationId: integrationId as string,
        externalUserRef: externalUserRef as string,
      });
      return data.sociumLearnerDetail;
    },
  });

export const useCreateSociumIntegration = () => {
  const queryClient = useQueryClient();
  return useMutation<IntegrationWithKey, Error, CreateSociumIntegrationInput>({
    mutationFn: async (input) => {
      const data = await graphqlFetch<
        { createSociumIntegration: IntegrationWithKey },
        { input: CreateSociumIntegrationInput }
      >(CREATE_SOCIUM_INTEGRATION, { input });
      return data.createSociumIntegration;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SOCIUM_KEY }),
  });
};

export const useUpdateSociumIntegration = () => {
  const queryClient = useQueryClient();
  return useMutation<
    SociumIntegration,
    Error,
    { integrationId: string; name?: string; allowedRedirectDomains?: string[] }
  >({
    mutationFn: async (input) => {
      const data = await graphqlFetch<
        { updateSociumIntegration: SociumIntegration },
        { input: typeof input }
      >(UPDATE_SOCIUM_INTEGRATION, { input });
      return data.updateSociumIntegration;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SOCIUM_KEY }),
  });
};

export const useRotateSociumIntegrationKey = () => {
  const queryClient = useQueryClient();
  return useMutation<IntegrationWithKey, Error, string>({
    mutationFn: async (integrationId) => {
      const data = await graphqlFetch<
        { rotateSociumIntegrationKey: IntegrationWithKey },
        { integrationId: string }
      >(ROTATE_SOCIUM_INTEGRATION_KEY, { integrationId });
      return data.rotateSociumIntegrationKey;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SOCIUM_KEY }),
  });
};

export const useSetSociumIntegrationStatus = () => {
  const queryClient = useQueryClient();
  return useMutation<
    SociumIntegration,
    Error,
    { integrationId: string; status: 'ACTIVE' | 'INACTIVE' }
  >({
    mutationFn: async (input) => {
      const data = await graphqlFetch<
        { setSociumIntegrationStatus: SociumIntegration },
        { input: typeof input }
      >(SET_SOCIUM_INTEGRATION_STATUS, { input });
      return data.setSociumIntegrationStatus;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SOCIUM_KEY }),
  });
};
